import React, { createContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import authService from '../services/authService';

export const AuthContext = createContext();

const clearTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('sessionKind');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // 'ok' | 'verify' (enter a code) | 'setup' (enrol an authenticator app)
  const [mfaStatus, setMfaStatus] = useState('ok');

  // The profile endpoint is exempt from the MFA gate, so it always reports the current status.
  const refreshProfile = useCallback(async () => {
    const profile = await authService.getProfile();
    setUser(profile);
    setMfaStatus(profile.mfa_status || 'ok');
    return profile;
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      if (localStorage.getItem('token')) {
        try {
          await refreshProfile();
        } catch (error) {
          clearTokens();
        }
      }
      setLoading(false);
    };
    loadUser();
  }, [refreshProfile]);

  // Raised by the API client when a request is rejected with mfa_required / mfa_setup_required.
  useEffect(() => {
    const onMfa = (e) => setMfaStatus(e.detail);
    window.addEventListener('auth:mfa', onMfa);
    return () => window.removeEventListener('auth:mfa', onMfa);
  }, []);

  const login = async (email, password) => {
    const res = await authService.login(email, password);
    localStorage.setItem('token', res.access_token);
    localStorage.removeItem('sessionKind');
    if (res.refresh_token) localStorage.setItem('refreshToken', res.refresh_token);
    // Fetch the profile so role and MFA status come from one place.
    return refreshProfile();
  };

  // Used after redeeming an emailed link: the API has issued a short session for a passwordless applicant.
  const loginWithToken = async (accessToken) => {
    localStorage.setItem('token', accessToken);
    localStorage.removeItem('refreshToken');
    localStorage.setItem('sessionKind', 'link');
    return refreshProfile();
  };

  const verifyMfa = async (code) => {
    await authService.verifyMfa(code);
    return refreshProfile();
  };

  const register = async (data) => {
    return await authService.register(data);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (e) {
      // Ignore errors on logout
    } finally {
      clearTokens();
      setUser(null);
      setMfaStatus('ok');
      window.location.href = '/';
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    userRole: user?.role,
    mfaStatus,
    login,
    loginWithToken,
    verifyMfa,
    refreshProfile,
    register,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};
